import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Chip, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, FormControl, InputLabel, Select, MenuItem, Stack, Divider
} from '@mui/material';
import { Computer as ComputerIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import equipmentService from '../../services/equipmentService';
import SkeletonBlock from '../ui/SkeletonBlock';
import EmptyState from '../ui/EmptyState';
import { useToast } from '../ToastProvider';

// Read-only field display
const ReadOnlyField = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>
      {value || '—'}
    </Typography>
  </Box>
);

export default function MyEquipment() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const toast = useToast();

  // Edit dialog
  const [selectedItem, setSelectedItem] = useState(null);
  const [editForm, setEditForm] = useState({ serialNumber: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [assignmentsData, categoriesData] = await Promise.all([
        equipmentService.getMyAssignments(categoryFilter || null),
        equipmentService.getCategories()
      ]);
      setItems(Array.isArray(assignmentsData) ? assignmentsData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (e) {
      setError(e?.response?.data || e.message || 'Failed to load equipment');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openEdit = (item) => {
    setSelectedItem(item);
    setEditForm({
      model: item.model || '',
      serialNumber: item.serialNumber || '',
      notes: item.notes || ''
    });
  };

  const closeEdit = () => {
    setSelectedItem(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await equipmentService.updateMyEquipment(selectedItem.id, editForm);
      toast.success('Equipment updated');
      closeEdit();
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <SkeletonBlock lines={5} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          My Assigned Equipment ({items.length})
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Filter by Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {items.length === 0 ? (
        <EmptyState
          icon={<ComputerIcon sx={{ fontSize: 48, color: 'text.secondary' }} />}
          title="No Equipment Assigned"
          description={categoryFilter ? "No equipment in this category." : "You don't have any equipment assigned to you yet."}
        />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Serial Number</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell>Warranty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  onClick={() => openEdit(item)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Chip label={item.categoryName} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {item.subcategoryName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.brand} {item.model}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.serialNumber || <em style={{color:'#999'}}>Not set</em>}</TableCell>
                  <TableCell>
                    {item.assignedDate ? format(new Date(item.assignedDate), 'dd.MM.yyyy') : '-'}
                  </TableCell>
                  <TableCell>
                    {item.warrantyExpiration ? (
                      <Chip
                        label={format(new Date(item.warrantyExpiration), 'dd.MM.yyyy')}
                        size="small"
                        color={new Date(item.warrantyExpiration) > new Date() ? 'success' : 'error'}
                        variant="outlined"
                      />
                    ) : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={!!selectedItem}
        onClose={closeEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        {selectedItem && (
          <>
            <DialogTitle>
              Edit Equipment
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
              {/* Read-only section */}
              <Box sx={{
                bgcolor: 'grey.50',
                borderRadius: 1.5,
                p: 2.5,
                mb: 3
              }}>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                  Equipment Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <ReadOnlyField label="Category" value={selectedItem.categoryName} />
                  <ReadOnlyField label="Type" value={selectedItem.subcategoryName} />
                  <ReadOnlyField label="Brand" value={selectedItem.brand} />
                  <ReadOnlyField
                    label="Assigned Date"
                    value={selectedItem.assignedDate ? format(new Date(selectedItem.assignedDate), 'dd.MM.yyyy') : null}
                  />
                  <ReadOnlyField
                    label="Warranty Until"
                    value={selectedItem.warrantyExpiration ? format(new Date(selectedItem.warrantyExpiration), 'dd.MM.yyyy') : null}
                  />
                </Box>
              </Box>

              {/* Editable section */}
              <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Details
              </Typography>
              <Stack spacing={2.5}>
                <TextField
                  label="Model"
                  fullWidth
                  value={editForm.model}
                  onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                  placeholder="Enter model"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Serial Number"
                  fullWidth
                  value={editForm.serialNumber}
                  onChange={(e) => setEditForm({ ...editForm, serialNumber: e.target.value })}
                  placeholder="Enter serial number"
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Any notes about this equipment..."
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={closeEdit} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

